/* eslint-disable @typescript-eslint/ban-types */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function appendFormData(formData: FormData, data: any, parentKey = ''): FormData {
  if (Array.isArray(data)) {
    data.forEach(item => {
      appendFormData(formData, item, `${parentKey}`);
    });
  } else if (data instanceof File) {
    formData.append(parentKey, data);
  } else if (data !== null && typeof data === 'object') {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        appendFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
      }
    }
  } else {
    formData.append(parentKey, data);
  }

  return formData;
}

export class ApiService {
  protected instance: AxiosInstance;

  private host: string;

  constructor(host: string) {
    this.host = host;
    this.instance = axios.create();
  }

  private updateInterceptors() {
    this.instance.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
  }

  protected resolveApiPath(path: string): string {
    return path;
  }

  protected async get(url: string, data?: AxiosRequestConfig): Promise<AxiosResponse> {
    this.updateInterceptors();
    return this.instance.get(`${this.host}${this.resolveApiPath(url)}`, data);
  }

  protected async post(url: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    this.updateInterceptors();
    return this.instance.post(`${this.host}${this.resolveApiPath(url)}`, data, config);
  }

  protected async put(url: string, data?: object): Promise<AxiosResponse> {
    this.updateInterceptors();
    return this.instance.put(`${this.host}${this.resolveApiPath(url)}`, data);
  }

  protected async delete(url: string, data?: object): Promise<AxiosResponse> {
    this.updateInterceptors();
    return this.instance.delete(`${this.host}${this.resolveApiPath(url)}`, { data });
  }

  protected async patch(url: string, data?: object, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    this.updateInterceptors();
    return this.instance.patch(`${this.host}${this.resolveApiPath(url)}`, data, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async postFormData(url: string, data?: any): Promise<AxiosResponse> {
    return this.rawMultipart('post', this.resolveApiPath(url), data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async patchFormData(url: string, data?: any): Promise<AxiosResponse> {
    return this.rawMultipart('patch', this.resolveApiPath(url), data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async rawMultipart(method: string, url: string, data: any): Promise<AxiosResponse> {
    const formData = appendFormData(new FormData(), data);
    this.updateInterceptors();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.instance({
      method,
      url: `${this.host}${this.resolveApiPath(url)}`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}
